import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import {
  ErrorResponse,
  IAddress,
  IIoTruckNeedFilling,
  IOrder,
  ISidebar,
  ITable,
  ServerResponse,
} from "../models/models";
import { authActions } from "./auth/auth.slice";
import { Socket, io } from "socket.io-client";
import { trucksActions } from "./trucks/trucks.slice";
import { store } from "../store";

const API_URL = process.env.REACT_APP_API_URL;
export let socket: Socket | null = null;

const baseQuery = (baseUrl?: string) =>
  fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers) => {
      headers.set("Accept", "application/json");
      const token = localStorage.getItem("accessToken");
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  });

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError & ErrorResponse,
  { replacedUrl?: boolean }
> = async (args, api, extraOptions): Promise<any> => {
  let result = await baseQuery(
    extraOptions?.replacedUrl
      ? API_URL?.replace("remote-control-panel/", "")
      : API_URL
  )(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    localStorage.removeItem("accessToken");
    api.dispatch(authActions.setAuth(false));
    if (socket) {
      socket?.disconnect();
    }
    // try to get a new token
    // const refreshResult = await baseQuery('/tokens/refresh', api, extraOptions)
    // if (refreshResult.data) {
    // store the new token
    // localStorage.setItem('token', token)
    // api.dispatch(tokenReceived(refreshResult.data))
    // retry the initial query
    // result = await baseQuery(args, api, extraOptions)
    // } else {
    // api.dispatch(loggedOut())
    // }
  }
  return result;
};

export const api = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "naturalPerson",
    "fop",
    "legalPerson",
    "trucker",
    "truck",
    "truck-type",
    "settings",
    "products",
    "orders",
    "order",
    "gas-stations",
    "payments",
    "trailer",
    "trailer-type",
    "notifications",
    "parties",
    "refuellings",
    "time-range",
    "delivery-rate",
    "website-products",
    "dashboard",
    "fuel-report",
    "balance-report",
    "corporate-clients",
    "freeDelivery",
    "fuelDiscount",
    "roles",
    "permissions",
    "admins",
    "currentUser",
    "region",
    "banner",
    "currentOrders",
    "analytics",
    "fiscal-products",
    "feedback-forms"
  ],
  endpoints: (builder) => ({
    getAddresses: builder.query<ITable<IAddress>, { id: any }>({
      query: ({ id }) => ({
        url: `users/${id}/addresses`,
      }),
      transformResponse: (response: ServerResponse<ITable<IAddress>>) =>
        response.data,
    }),
    getClientOrders: builder.query<ITable<IOrder>, any>({
      query: (params) => ({
        url: `orders`,
        params,
      }),
      transformResponse: (response: ServerResponse<ITable<IOrder>>) => ({
        ...response.data,
        data: response.data.data.map((i) => ({ ...i, entityId: i.id })),
      }),
    }),
    getSidebar: builder.query<ISidebar, any>({
      query: (params) => ({
        url: `sidebar`,
        params,
      }),
      transformResponse: (response: ServerResponse<ISidebar>) => response.data,
      providesTags: ["truck", "trailer", "notifications"],
    }),
    changeNotificationsView: builder.mutation<any, string>({
      query: (type) => ({
        url: `notifications/${type}`,
        method: "PUT",
      }),
      invalidatesTags: ["notifications"],
    }),
    getPermissionsList: builder.query<
      Array<{ name: string; value: string }>,
      any
    >({
      query: (params) => ({
        url: "permissions",
        params,
      }),
      providesTags: ["permissions"],
      transformResponse: (
        response: ServerResponse<Array<{ name: string; value: string }>>
      ) => response.data,
    }),
    subscribeToEvents: builder.query<any, void>({
      queryFn: async () => ({ data: null }),
      async onCacheEntryAdded(
        _arg,
        { dispatch, cacheDataLoaded, updateCachedData, cacheEntryRemoved }
      ) {
        const token = localStorage.getItem("accessToken");

        if (!token) return;

        const checkToken = async () => {
          const res = await fetch(
            `${API_URL?.replace("remote-control-panel/", "")}users/user_id`,
            {
              headers: {
                Accept: "application/json",
                authorization: `Bearer ${token}`,
              },
            }
          );

          return res.status;
        };

        const authStatus = await checkToken();

        if (authStatus !== 200) return;

        socket = io(process.env.REACT_APP_SOCKET_URL || "", {
          auth: { token },
        });

        try {
          await cacheDataLoaded;

          socket.on("connect", () => {
            console.log("io connect");
            dispatch(api.util.invalidateTags(["dashboard", "trucker", "currentOrders"]));
          });

          socket.on("reconnect", (attempt) => {
            console.log("io reconnect: ", attempt);
            dispatch(api.util.invalidateTags(["dashboard", "trucker", "currentOrders"]));
          });

          socket.on("connect_error", async (err) => {
            console.log("io connect_error: ", err);
            const authStatus = await checkToken();
            if (authStatus === 200) {
              /* @ts-ignore-start */
              socket.auth.token = token;
              /* @ts-ignore-end */
              socket.connect();
              dispatch(api.util.invalidateTags(["dashboard", "trucker", "currentOrders"]));
            }
          });

          socket.on("disconnect", async (reason) => {
            console.log("io server_disconnect: ", reason);
            const authStatus = await checkToken();
            if (authStatus === 200) {
              if (reason === "io server disconnect") {
                /* @ts-ignore-start */
                socket.auth.token = token;
                /* @ts-ignore-end */
                socket.connect();
              }
              dispatch(api.util.invalidateTags(["dashboard", "trucker", "currentOrders"]));
            }
          });

          socket.on("truck_need_filling", (res: IIoTruckNeedFilling) => {
            dispatch(trucksActions.setTruckNeedFilling(res));
          });

          socket.on("change_truck_location_on_dashboard", () => {
            dispatch(api.util.invalidateTags(["dashboard"]));
          });

          socket.on("change_order_addresses_on_dashboard", () => {
            dispatch(api.util.invalidateTags(["dashboard"]));
          });

          socket.on("change_truck", () => {
            dispatch(api.util.invalidateTags(["trucker"]));
          });

          socket.on("change_current_order", () => {
            dispatch(api.util.invalidateTags(["currentOrders"]));
          });
        } catch (error) {
          console.log("error", error);
        }

        await cacheEntryRemoved;
        // socket.close()
      },
    }),
  }),
});

export const {
  useGetAddressesQuery,
  useGetClientOrdersQuery,
  useGetSidebarQuery,
  useChangeNotificationsViewMutation,
  useGetPermissionsListQuery,
  useSubscribeToEventsQuery,
  useLazySubscribeToEventsQuery,
} = api;
